import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import {
  LayoutDashboard,
  Package,
  Key,
  Laptop,
  CreditCard,
  FileText,
  LifeBuoy,
  User,
  Layers,
  Sliders,
  Users,
  Lock,
  Activity,
  Menu,
  X,
  Download,
  ImagePlus,
  Building2,
  ClipboardList,
} from 'lucide-react';

interface SidebarProps {
  portal: 'customer' | 'admin';
}

export const Sidebar: React.FC<SidebarProps> = ({ portal }) => {
  const { t } = useLanguage();
  const [mobileOpen, setMobileOpen] = useState(false);

  const customerLinks = [
    { label: t('nav.dashboard'), path: '/customer', icon: LayoutDashboard },
    { label: t('nav.myProducts'), path: '/customer/products', icon: Package },
    { label: t('nav.licenses'), path: '/customer/licenses', icon: Key },
    { label: t('customer.boundDevices'), path: '/customer/devices', icon: Laptop },
    { label: t('nav.downloads'), path: '/customer/downloads', icon: Download },
    { label: t('nav.subscriptions'), path: '/customer/subscriptions', icon: CreditCard },
    { label: t('nav.invoices'), path: '/customer/invoices', icon: FileText },
    { label: t('portal.helpTitle'), path: '/customer/support', icon: LifeBuoy },
    { label: t('account.title'), path: '/customer/profile', icon: User },
  ];

  const adminLinks = [
    { label: t('nav.dashboard'), path: '/admin', icon: LayoutDashboard },
    { label: t('nav.customers'), path: '/admin/users', icon: Users },
    { label: t('nav.products'), path: '/admin/apps', icon: Layers },
    { label: t('nav.media'), path: '/admin/media', icon: ImagePlus },
    { label: t('nav.releases'), path: '/admin/releases', icon: Download },
    { label: t('dist.downloadAuditNav'), path: '/admin/downloads', icon: ClipboardList },
    { label: t('nav.licenses'), path: '/admin/licenses', icon: Lock },
    { label: t('nav.devices'), path: '/admin/devices', icon: Laptop },
    { label: t('nav.trials'), path: '/admin/trials', icon: CreditCard },
    { label: t('nav.subscriptions'), path: '/admin/subscriptions', icon: FileText },
    { label: t('admin.auditLogs'), path: '/admin/auditlogs', icon: Activity },
    { label: t('nav.featureFlags'), path: '/admin/settings', icon: Sliders },
    { label: t('admin.settings.nav'), path: '/admin/settings/platform', icon: Building2 },
    { label: t('account.title'), path: '/admin/account', icon: User },
  ];

  const links = portal === 'admin' ? adminLinks : customerLinks;
  const portalTitle = portal === 'admin' ? t('admin.title') : t('customer.title');

  const nav = (
    <>
      <div className="px-2.5 pb-5 mb-1 border-b border-line dark:border-line-dark">
        <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-brand-500">
          BeCoder
        </span>
        <h3 className="font-display text-sm font-bold text-ink dark:text-paper mt-1.5">{portalTitle}</h3>
      </div>

      <nav className="space-y-1" aria-label={`${portal} navigation`}>
        {links.map((link) => {
          const Icon = link.icon;
          return (
            <NavLink
              key={link.path}
              to={link.path}
              end={
                link.path === '/customer' ||
                link.path === '/admin' ||
                link.path === '/admin/settings'
              }
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                `group relative flex items-center gap-2.5 px-2.5 py-2.5 rounded-md text-xs font-semibold transition-[background-color,color,box-shadow] duration-150 ${
                  isActive
                    ? 'bg-brand-500/10 text-brand-700 dark:text-brand-300 shadow-[var(--bc-shadow)]'
                    : 'text-ink/70 dark:text-paper/70 hover:text-brand-700 dark:hover:text-brand-300 hover:bg-brand-500/8'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {isActive && (
                    <span
                      className="absolute start-0 top-1.5 bottom-1.5 w-0.5 rounded-full bg-brand-500"
                      aria-hidden
                    />
                  )}
                  <Icon
                    className="w-4 h-4 shrink-0 text-brand-500 transition-transform duration-150 group-hover:scale-105"
                    strokeWidth={isActive ? 2 : 1.75}
                  />
                  <span>{link.label}</span>
                </>
              )}
            </NavLink>
          );
        })}
      </nav>
    </>
  );

  return (
    <>
      <div className="md:hidden px-4 pt-4">
        <button
          type="button"
          onClick={() => setMobileOpen(!mobileOpen)}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-line dark:border-line-dark bg-white dark:bg-ink-elevated text-xs font-semibold shadow-[var(--bc-shadow)]"
          aria-expanded={mobileOpen}
        >
          {mobileOpen ? (
            <X className="w-4 h-4 text-brand-500" />
          ) : (
            <Menu className="w-4 h-4 text-brand-500" />
          )}
          {portalTitle}
        </button>
        {mobileOpen && (
          <aside className="mt-3 p-4 rounded-lg bg-white dark:bg-ink-elevated border border-line dark:border-line-dark space-y-4 shadow-[var(--bc-shadow)]">
            {nav}
          </aside>
        )}
      </div>

      <aside className="w-60 shrink-0 border-e border-line/80 dark:border-white/8 p-4 space-y-5 hidden md:block min-h-[calc(100vh-3.5rem)] bg-white/55 dark:bg-[rgba(18,21,30,0.72)] backdrop-blur-md">
        {nav}
      </aside>
    </>
  );
};
